import { airportsOrder, branchesOrder } from '@/data/branchesPageData'
import { mapMegaMenuContent } from '@/components/Global/MegaMenu/megaMenuContentMapper'
import type { IMegaMenuLinksBySection } from '@/modules/adminModule/types/megaMenuTree'

// Matches the ids used as keys in MEGA_MENU_LINK_META (megaMenuContentMapper.ts) for the
// four categories that get live CMSLocation data appended to their static CMS sublinks.
const CUSTOM_KEYS = ['bus-rental', 'cities', 'airports', 'delivery-van-rental'] as const
type CustomKey = (typeof CUSTOM_KEYS)[number]

interface ICustomMenuItem {
	to: string
	title?: string
	translation_key_title?: string
}

const RENTAL_LENGTH_BY_LINK: Record<string, number> = {
	'2months': 2,
	'3months': 3,
	'6months': 6,
}

export const useMegaMenu = () => {
	const { locale, t } = useI18n()
	const localePath = useLocalePath()
	const CMSLocationsStore = useCMSLocationsStore()
	const carRentalStore = useCarRentalStore()
	const CMSLocations = ref<ICMSLocationModel[] | undefined>(undefined)

	const { data: megaMenuSection } = useAsyncData('mega-menu-content-section', () =>
		ApiService.fetchPageSectionByName('menu', 'mega-menu', 'mega-menu'),
	)

	const linksBySection = computed<IMegaMenuLinksBySection>(
		() => (megaMenuSection.value?.translations?.[0]?.content as IMegaMenuLinksBySection | undefined) ?? {},
	)

	const computedMenu = computed(() => mapMegaMenuContent(linksBySection.value, locale.value))

	const areCustomItems = (key: string): key is CustomKey => CUSTOM_KEYS.includes(key as CustomKey)

	const getURLforLocation = (item: ICMSLocationModel) => {
		const urlLocale = item[`isActive${locale.value.toUpperCase()}`] ? item[`url${locale.value.toUpperCase()}`] : undefined
		const url = item.urlPL

		if (!url && !urlLocale) return undefined
		return urlLocale
			? localePath({ name: 'car-rental-urlSlug', params: { urlSlug: urlLocale } }, locale.value)
			: localePath({ name: 'car-rental-urlSlug', params: { urlSlug: url } }, 'pl')
	}

	const isMenuLocation = (l: ICMSLocationModel) => l.showInMenu && l[getCMSLocationIsActiveKey(locale.value)]

	const menuLocations = computed(() => CMSLocations.value?.filter(isMenuLocation) ?? [])

	// Same category rules as the /locations page (src/pages/locations/index.vue), so the mega
	// menu and the full locations page always agree on what counts as a bus/delivery-van/airport/city.
	const locationTitle = (l: ICMSLocationModel) => (locale.value === 'pl' ? l.namePL : (l.nameEN ?? l.namePL))

	const sortByOrder = (order: string[]) => (a: ICMSLocationModel, b: ICMSLocationModel) => {
		const rank = (l: ICMSLocationModel) => {
			const index = order.indexOf(l.urlPL)
			return index === -1 ? order.length : index
		}
		return rank(a) - rank(b)
	}

	const sortByName = (a: ICMSLocationModel, b: ICMSLocationModel) => locationTitle(a).toLowerCase().localeCompare(locationTitle(b).toLowerCase())

	// Title template (components.nav.mega_menu.<key>, with a {value} placeholder for the location
	// name) per dynamic list - omitted for airports, which render the bare location name.
	const titleTemplate = (key: string) => (value: string) => t(`components.nav.mega_menu.${key}`, { value })

	const buildCustomMenuItems = (
		filterFn: (l: ICMSLocationModel) => boolean,
		sortFn: (a: ICMSLocationModel, b: ICMSLocationModel) => number,
		formatTitle: (value: string) => string = (value) => value,
	) => {
		const items = menuLocations.value
			.filter(filterFn)
			.sort(sortFn)
			.reduce<ICustomMenuItem[]>((acc, location) => {
				const to = getURLforLocation(location)
				if (to) acc.push({ to, title: formatTitle(locationTitle(location)) })
				return acc
			}, [])

		if (items.length === 0) return []

		items.push({ to: localePath('locations'), translation_key_title: 'see_all_locations' })
		return items
	}

	const busesData = computed(() => buildCustomMenuItems((l) => l.passengerBus, sortByName, titleTemplate('passenger_bus_location_title')))

	const deliveryBusesData = computed(() => buildCustomMenuItems((l) => l.deliveryBus, sortByName, titleTemplate('delivery_van_location_title')))

	const airportsData = computed(() =>
		buildCustomMenuItems((l) => l.icon === 'airplane' && !l.otherLocation && !l.deliveryBus && !l.passengerBus, sortByOrder(airportsOrder)),
	)

	const branchesData = computed(() =>
		buildCustomMenuItems(
			(l) => l.icon !== 'airplane' && !l.otherLocation && !l.deliveryBus && !l.passengerBus,
			sortByOrder(branchesOrder),
			titleTemplate('city_location_title'),
		),
	)

	const getCustomItemsFor = (category: { translation_key_title?: string } | null | undefined) => {
		if (!category) return []
		switch (category.translation_key_title) {
			case 'bus-rental':
				return busesData.value
			case 'delivery-van-rental':
				return deliveryBusesData.value
			case 'cities':
				return branchesData.value
			case 'airports':
				return airportsData.value
			default:
				return []
		}
	}

	const handleRentalLengthFromLink = (link?: string) => {
		carRentalStore.setRangeValue(RENTAL_LENGTH_BY_LINK[link ?? ''] ?? 1)
	}

	const ensureLocationsLoaded = async () => {
		if (!CMSLocations.value?.length) {
			CMSLocations.value = await CMSLocationsStore.fetchCMSLocationsForScope('menu')
		}
	}

	return {
		computedMenu,
		areCustomItems,
		getCustomItemsFor,
		handleRentalLengthFromLink,
		ensureLocationsLoaded,
	}
}
